import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { PatientDocument } from './types';
import { DocumentGroup, groupSelectionState } from './grouping';
import { plural, toDisplayDate } from './format';

interface Props {
  groups: DocumentGroup[];
  loading: boolean;
  selected: Set<string>;
  onToggleDocument: (uuid: string) => void;
  onToggleGroup: (group: DocumentGroup) => void;
  onSelectAll: () => void;
  onClear: () => void;
  hasPeriodSelected: boolean;
}

/**
 * SQ-167, step 3 — WHAT.
 *
 * One list, grouped by kind, everything ticked. This replaces the Discipline and
 * Document type dropdowns, which were static lists offering options the selection
 * might not contain — pick OT on a PT-only resident and you got an empty ZIP with no
 * warning. Here a kind that is not present simply is not listed, so that cannot happen.
 */
export const DocumentPicker: React.FC<Props> = ({
  groups,
  loading,
  selected,
  onToggleDocument,
  onToggleGroup,
  onSelectAll,
  onClear,
  hasPeriodSelected,
}) => {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (kind: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  if (!hasPeriodSelected) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        Choose a period above to see what is available.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        Loading documents…
      </Typography>
    );
  }

  if (groups.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        No documents in the selected period.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {groups.map((group) => {
        const state = groupSelectionState(group, selected);
        const isOpen = expanded.has(group.kind);
        return (
          <Box key={group.kind}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
            >
              <FormControlLabel
                sx={{ flex: 1, ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={state === 'all'}
                    indeterminate={state === 'some'}
                    onChange={() => onToggleGroup(group)}
                    inputProps={{ 'aria-label': `${group.label} group` }}
                  />
                }
                label={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {group.label}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={group.documents.length}
                    />
                  </Stack>
                }
              />
              <IconButton
                size="small"
                onClick={() => toggleExpanded(group.kind)}
                aria-label={isOpen ? `Collapse ${group.label}` : `Expand ${group.label}`}
              >
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Stack>

            <Collapse
              in={isOpen}
              unmountOnExit
            >
              <Stack
                spacing={0}
                sx={{ pl: 4, pb: 1 }}
              >
                {group.documents.map((doc: PatientDocument) => (
                  <FormControlLabel
                    key={doc.uuid}
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(doc.uuid)}
                        onChange={() => onToggleDocument(doc.uuid)}
                        inputProps={{ 'aria-label': doc.documentName }}
                      />
                    }
                    label={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {toDisplayDate(doc.dateOfService)} · {doc.discipline || '—'}
                        </Typography>
                        {/* SQ-181: a list that grows by twenty has to say which twenty. */}
                        {doc.heldFromBilling && (
                          <Chip
                            size="small"
                            label="Held"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 11 }}
                          />
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
      <Stack
        direction="row"
        spacing={2}
        sx={{ pt: 0.5, pl: 0.5 }}
      >
        <Button
          size="small"
          sx={{ minWidth: 0, p: 0, fontWeight: 700 }}
          onClick={onSelectAll}
        >
          Select all
        </Button>
        <Button
          size="small"
          color="inherit"
          sx={{ minWidth: 0, p: 0, fontWeight: 700, color: 'text.secondary' }}
          onClick={onClear}
        >
          Clear
        </Button>
      </Stack>
    </Stack>
  );
};
